/**
 * @example
 * '/abc/g' -> /abc/g
 * @param {String} str - stringified regexp
 * @return {RegExp} - real regexp
 */
const stringToRegexp = (str) => {
    const lastSlash = str.lastIndexOf('/');
    return new RegExp(
        str.slice(1, lastSlash),
        str.slice(lastSlash + 1)
    );
};

/**
 * Makes single-char regular express optional
 * @example
 * /\d/ -> /\d?/
 * @param {RegExp} charRegexp
 * @return {RegExp}
 */
const makeRegexpOptional = (charRegexp) => (
    stringToRegexp(
        charRegexp.toString()
            .replace(
                /.(\/)[gmiyus]{0,6}$/,
                (match) => match.replace('/', '?/')
            )
    )
);

/**
 * @param {String} char
 * @return {String}
 */
const escapeIfNeeded = (char) => (
    '[\\^$.|?*+()'.indexOf(char) > -1
        ? `\\${char}`
        : char
);

/**
 * Wraps static character to RegExp
 * @param {String} char
 * @return {RegExp}
 */
const charRegexp = (char) => new RegExp(`/[${escapeIfNeeded(char)}]/`);

/**
 * Determines if value is regular expression
 * @param {String|RegExp} entity
 * @return {boolean}
 */
const isRegexp = (entity) => entity instanceof RegExp;

/**
 * @param {String|RegExp} char
 * @return {RegExp}
 */
const castToRegexp = (char) => (isRegexp(char) ? char : charRegexp(char));
/**
 * A special object to identify next character as optional
 * For example `?`
 *
 * @type {Object}
 */
const NEXT_CHAR_OPTIONAL = {
    __nextCharOptional__ : true
};

/**
 * @type {Object<String,RegExp|NEXT_CHAR_OPTIONAL>}
 */
const defaultMaskReplacers = {
    '#' : /\d/,
    A   : /[a-z]/i,
    N   : /[a-z0-9]/i,
    '?' : NEXT_CHAR_OPTIONAL,
    X   : /./
};

function maskToRegExpMask (mask, maskReplacers = defaultMaskReplacers) {
    return mask
        .map((char, index, array) => {
            const maskChar = maskReplacers[char] || char;
            const previousChar = array[index - 1];
            const previousMaskChar = maskReplacers[previousChar] || previousChar;
            if (maskChar === NEXT_CHAR_OPTIONAL) {
                return null;
            }
            if (previousMaskChar === NEXT_CHAR_OPTIONAL) {
                return makeRegexpOptional(castToRegexp(maskChar));
            }
            return maskChar;
        })
        .filter(Boolean);
}

/**
 * Converts mask from `v-mask` string format to `text-mask-core` format
 * @param {String} stringMask
 * @param {object<string, RegExp|NEXT_CHAR_OPTIONAL>} maskReplacers
 * @return {RegExp[]}
 */
export function stringMaskToRegExpMask (stringMask, maskReplacers = defaultMaskReplacers) {
    return maskToRegExpMask(stringMask.split(''), maskReplacers);
}

/**
 * Converts mask from `v-mask` array format to `text-mask-core` format
 * @param {Array.<String|RegExp>} arrayMask
 * @param {object<string, RegExp|NEXT_CHAR_OPTIONAL>} maskReplacers
 * @return {RegExp[]}
 */
export function arrayMaskToRegExpMask (arrayMask, maskReplacers = defaultMaskReplacers) {
    const flattenedMask = arrayMask
        .map((part) => {
            if (part instanceof RegExp) {
                return part;
            }
            if (typeof part === 'string') {
                return part.split('');
            }
            return null;
        })
        .filter(Boolean)
        .reduce((mask, part) => mask.concat(part), []);

    return maskToRegExpMask(flattenedMask, maskReplacers);
}
